import type { AxiosResponse } from 'axios';
import type {
  PaginatedResponse,
  Visita,
  VisitaAllegato,
  VisitaCreate,
  VisitaUpdate,
} from '@offertify/core';
import { getApiClient } from '../configure';

export interface GetVisiteParams {
  page?: number;
  limit?: number;
  from_date?: string;
  to_date?: string;
  dashboard_only?: boolean;
  kpi?: string;
  reference_date?: string;
  exclude_cancelled?: boolean;
  origine?: 'manuale' | 'app';
  interne?: boolean;
  stato?: string;
  tipo?: string;
  cliente_id?: string;
  user_id?: string;
  offerta_id?: string;
}

/** Filters for the Excel export; a subset of GetVisiteParams (no pagination). */
export type ExportVisiteParams = Omit<GetVisiteParams, 'page' | 'limit'>;

/**
 * Calendar window params. `from_date`/`to_date` bound the visible month grid and
 * are required; the rest mirror the list filters. No pagination — the endpoint
 * returns every activity in the window so days can be bucketed accurately.
 */
export interface GetCalendarParams {
  from_date: string;
  to_date: string;
  dashboard_only?: boolean;
  kpi?: string;
  reference_date?: string;
  exclude_cancelled?: boolean;
  origine?: 'manuale' | 'app';
  interne?: boolean;
  tipo?: string;
  stato?: string;
  cliente_id?: string;
  user_id?: string;
}

/** Resolve the shared transport lazily so the app can configure it at startup. */
function http() {
  return getApiClient().client;
}

/**
 * Visite (visits/activities) domain endpoints. Split out of the web api.ts
 * monolith; identical routes and payloads, now platform-agnostic (web + mobile).
 */
export const visiteAPI = {
  getVisite: (params?: GetVisiteParams): Promise<AxiosResponse<PaginatedResponse<Visita>>> =>
    http().get('/visite', { params }),

  getVisita: (id: string): Promise<AxiosResponse<Visita>> => http().get(`/visite/${id}`),

  // Unpaginated activities within a date window, for the calendar view.
  getCalendar: (params: GetCalendarParams): Promise<AxiosResponse<Visita[]>> =>
    http().get('/visite/calendar', { params }),

  createVisita: (data: VisitaCreate): Promise<AxiosResponse<Visita>> =>
    http().post('/visite', data),

  updateVisita: (id: string, data: VisitaUpdate): Promise<AxiosResponse<Visita>> =>
    http().put(`/visite/${id}`, data),

  // Soft delete (backend marks is_deleted; never a physical delete).
  deleteVisita: (id: string, scope: 'singola' | 'serie' = 'singola'): Promise<AxiosResponse<void>> => http().delete(`/visite/${id}`, { params: { scope } }),

  // Excel (.xlsx) export of the sellers' activities matching the filters.
  exportVisite: (params?: ExportVisiteParams): Promise<AxiosResponse<Blob>> =>
    http().get('/visite/export', { params, responseType: 'blob' }),

  // .ics file for a single visit, to add to the phone's native calendar app.
  downloadIcs: (id: string): Promise<AxiosResponse<Blob>> =>
    http().get(`/visite/${id}/calendar.ics`, { responseType: 'blob' }),

  // --- Allegati (foto e documenti, issue #105) ---
  getAllegati: (id: string): Promise<AxiosResponse<VisitaAllegato[]>> =>
    http().get(`/visite/${id}/allegati`),

  /**
   * Multipart upload. `file` is a browser File/Blob on web; on React Native pass
   * the `{ uri, name, type }` object FormData accepts there.
   */
  uploadAllegato: (
    id: string,
    file: Blob | { uri: string; name: string; type: string },
    descrizione?: string,
    onUploadProgress?: (percent: number) => void,
  ): Promise<AxiosResponse<VisitaAllegato>> => {
    const formData = new FormData();
    formData.append('file', file as Blob);
    if (descrizione) formData.append('descrizione', descrizione);
    return http().post(`/visite/${id}/allegati`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (e) => onUploadProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
        : undefined,
    });
  },

  // Short-lived (5 min) presigned URL with attachment disposition.
  getAllegatoDownloadUrl: (id: string, allegatoId: string): Promise<AxiosResponse<{ url: string }>> =>
    http().get(`/visite/${id}/allegati/${allegatoId}/download`),

  // Soft delete (the S3 object is kept for audit).
  deleteAllegato: (id: string, allegatoId: string): Promise<AxiosResponse<void>> =>
    http().delete(`/visite/${id}/allegati/${allegatoId}`),
};
